import { useEffect, useRef } from "react";
import slide1 from "./../assets/slide_1.mp4";
import SEOLandingSection from "./SEOLandingSection";
import securityDataPrivacyImage from "./../assets/security-data-privacy-image.png";
import fashionReturnsRateImage from "./../assets/fashion-return-rates-image.png";
import aiFitPredictionImage from "./../assets/ai-fit-prediction-image.png";
import apparelSizingSoftwareImage from "./../assets/apparel-sizing-software-image.png";
import sizeRecommendationImage from "./../assets/size-recommendation-image.png";
import benefitsOfAIImage from "./../assets/benefits-of-ai-image.png";

const ShopifyAISizingSections = () => {
  const videoRef = useRef(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasPlayedRef.current && video.paused && !video.ended) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch(() => {});
            }
          }
        } else {
          if (!video.paused && !video.ended) {
            video.pause();
          }
        }
      },
      { threshold: 0.5 },
    );

    const handleEnded = () => {
      hasPlayedRef.current = true;
    };

    video.addEventListener("ended", handleEnded);
    observer.observe(video);

    return () => {
      video.removeEventListener("ended", handleEnded);
      observer.unobserve(video);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="font-normal text-[14px] leading-[23.4px] tracking-[0.17px] text-[#4A5565]">
      <SEOLandingSection
        heroImage={fashionReturnsRateImage}
        bgColor="#EDF3FF"
        heading="Why Shopify Fashion Stores Face High Return Rates"
      >
        <div className="my-[10px]">
          <div>Apparel Shopify Brands face 30-40% return rates due to:</div>
          <ul className="">
            <li>• Sizing inconsistency</li>
            <li>• Lack of body-specific prediction</li>
            <li>• Bracketing behavior</li>
            <li>• Manual size charts</li>
            <li>• Fit uncertainty</li>
          </ul>
          <ul className="mt-[20px]">
            Eveness Fit Plug-In combats this with:
            <li>• Body measurement modeling</li>
            <li>• Garment fit prediction</li>
            <li>• Apparel fit analytics</li>
            <li>• Size recommendation engines</li>
            <li>• Seamless E-commerce integrations</li>
          </ul>
        </div>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={aiFitPredictionImage}
        bgColor="#EBFFF6"
        heading="What Is Shopify AI Sizing?"
      >
        <div>
          <div className="my-[20px]">
            Shopify AI sizing refers to an AI-powered size recommendation system
            integrated directly into a Shopify store that predicts the most
            accurate garment size based on shopper inputs and product-level fit
            data.
          </div>
          <ul className="">
            <li>Real-time size prediction</li>
            <li>SKU-level modeling</li>
            <li>Seamless Shopify integration</li>
            <li>Works across collections</li>
          </ul>
        </div>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={apparelSizingSoftwareImage}
        bgColor="#EFF2FF"
        heading="Apparel Sizing Software vs Traditional Size Charts"
      >
        <ul className="mt-[10px]">
          <li>Interactive user experience for SMS Marketing</li>
          <li>
            Anonymized segment led marketing (by sizing, body and face shape,
            color analysis, height, and more)
          </li>
          <li>Personalized with Agentic Capabilities</li>
        </ul>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={sizeRecommendationImage}
        bgColor="#FFE8DF"
        heading="Size Recommendation Software for Ecommerce Brands"
      >
        <ul className="text-[#47423D] mt-[10px]">
          <li>
            Incorrect sizing is one of the primary causes of fashion ecommerce
            returns
          </li>
          <li>Shoppers are more likely to purchase when sizing is clear.</li>
          <li>Size recommendation software reduces purchase uncertainty.</li>
          <li>
            Apparel e-commerce return rates typically range between 20–40%.
          </li>
        </ul>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={benefitsOfAIImage}
        bgColor="#EAEBFF"
        heading="Benefits of AI Sizing Software for Fashion Brands"
      >
        <ul className="text-[#47423D] mt-[10px]">
          <li>Reduce Returns</li>
          <li>Improve Conversion Rate</li>
          <li>Increase Customer Confidence</li>
          <li>Product-Level Fit Intelligence</li>
        </ul>
      </SEOLandingSection>

      <SEOLandingSection
        heading="Key Features of AI Apparel Sizing Software"
        videoSrc={slide1}
        videoRef={videoRef}
        videoIndex={0}
        ctaLink="/get-started"
        ctaText="Get Started"
      >
        <div className="space-y-4 ">
          <ul className="text-[#47423D] mt-[10px] space-y-2">
            <li>SKU-Level Modeling</li>
            <li>API integrations</li>
            <li>Privacy compliance</li>
            <li>Real-time predictions</li>
            <li>Style-specific accuracy</li>
            <li>Real-time predictions</li>
            <li>Cross-device compatibility</li>
          </ul>
        </div>
      </SEOLandingSection>

      <div className="pb-4 md:pb-6 bg-[#DADADA] mb-[40px] md:mb-[60px]">
        <SEOLandingSection
          heroImage={securityDataPrivacyImage}
          bgColor="#EDFCFF"
          heading="Security, Data Privacy & Compliance"
          source="security"
        >
          <div className="space-y-4 ">
            <ul className="text-[#47423D] mt-[10px] space-y-2">
              <li>GDPR alignment</li>
              <li>SOC2 readiness</li>
              <li>Data anonymization</li>
              <li>Secure cloud storage</li>
            </ul>
          </div>
        </SEOLandingSection>
      </div>
    </div>
  );
};

export default ShopifyAISizingSections;
